import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Circle, AlertCircle, ChevronDown, Image as ImageIcon, X, Zap, MinusCircle, Clock, HeartCrack, Flag, ShieldCheck, Ban, RotateCcw, Pencil } from 'lucide-react';
import { toast } from 'react-toastify';
import { workspaceAPI, coachDailyContactsAPI, stepExtrasAPI } from '../../../services/api';
import { resolvePhotoUrl } from '../../../utils/photoUtils';
import { fmtMDHM, asArray } from '../../service/sopShared';
import { S, TabIntro, card, btnPrimary, btnGhost, btnLink, field, SOP_STEPS, stepDef, NotDeployed, dueText, fmtYMD } from './wsShared';
import { TAB_EXPLAIN } from '../../tour/tourSteps';
import ReviewRoundTools from './ReviewRoundTools';
import StepShots, { SHOT_STEP_KEYS } from './StepShots';
import NextDatePanel from './NextDatePanel';
import { QrUploadButton } from './QrUpload';

// ② 服务进度 — the SOP 5.0 steps as a vertical stepper.
//   开始阶段 (sign … team_review) happens once; collapsed to 「开始阶段 ✓」 when done.
//   第 N 轮推荐 repeats: 两位老师核实 → 说明电话 → 交叉电话 → 约会反馈 → 复盘 / 约第二次 → 一对一 → 满 3 个月结单.
//   不合适（复盘）or 一对一中途分手 → the next round starts again at 两位老师核实.
//   一对一 → 关系稳定期 (3 months countdown) → 确认结单 → 已结单.
// Each row: status icon, name, 负责人, 时限 / 逾期; expanding shows the 完成标准,
// the evidence on file and the button that completes it.
// 管理员 (step.can_override from the API): a 「管理员操作」 group on every step — 标记完成 /
// 不算完成 / 恢复自动判断 (also on automatic steps) + 编辑备注; overridden rows carry a
// 「管理员已改 · …」 pill (read-only for everyone else).
//
// Props: profileId, steps (API Step[] or null when /workspace is missing),
// round, viewRound, onRound(n), rounds [{round, outcome}], serviceClosed,
// closedAt, exclusiveSince, closeDueAt, closeDaysLeft, isZh, meId, isSup,
// teachers [{id,name}], focusKey, onChanged, onGo(target).
// readOnly: the member is not the viewer's own (/sop-progress) — status ✓ only, nothing opens or ticks.

const STATUS = {
  done: { Icon: CheckCircle2, color: '#3F7D5A', zh: '已完成', en: 'Done' },
  current: { Icon: Circle, color: '#111111', zh: '进行中', en: 'Now' },
  overdue: { Icon: AlertCircle, color: '#9E2B25', zh: '已逾期', en: 'Overdue' },
  waiting: { Icon: Clock, color: '#93591A', zh: '稳定期', en: 'Waiting' },
  skipped: { Icon: MinusCircle, color: '#6E685F', zh: '未进行', en: 'Skipped' },
  todo: { Icon: Circle, color: '#A39E95', zh: '未开始', en: 'Later' },
};
const GO = {
  quick_card: ['quick_card', '去填速记卡', 'Fill the quick card'],
  love_language: ['quiz', '看测评报告', 'Assessments'],
  rec_call: ['contact:recommendation', '写推荐说明电话', 'Log the call'],
  cross_call: ['meets', '去约见记录', 'Meetings'],
  date_feedback: ['meets', '去约见记录', 'Meetings'],
  first_recs: ['recommend', '去推荐', 'Recommend'],
};
const OUTCOME_STYLE = {
  review: { bg: '#EFEAE2', fg: '#6E685F' },
  breakup: { bg: '#F3E3E1', fg: '#9E2B25' },
  closed: { bg: '#E6EEE8', fg: '#3F7D5A' },
};

export default function StepsTab({
  profileId, steps, round = 1, viewRound, onRound, rounds = [], serviceClosed = false, closedAt = null,
  exclusiveSince = null, closeDueAt = null, closeDaysLeft = null, roundMatches = [],
  isZh = true, meId, isSup, teachers = [], focusKey, onChanged, onGo,
  memberName = '', TimeInput = null, readOnly = false,
}) {
  const t = (zh, en) => (isZh ? zh : en);
  const [open, setOpen] = useState(focusKey || null);
  // { key, mode: 'tick' | 'ov_done' | 'ov_undone' | 'note', note, images, verified_by, choice }
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [breakup, setBreakup] = useState(null); // reason text while the dialog is open
  const fileRef = useRef(null);
  useEffect(() => { if (focusKey) setOpen(focusKey); }, [focusKey]);
  // 双方截图 (rec_call / cross_call / date_feedback). null = API not deployed → slots hidden.
  const [shots, setShots] = useState(null);
  useEffect(() => {
    if (readOnly) { setShots(null); return undefined; }
    let off = false;
    stepExtrasAPI.attachments(profileId)
      .then((r) => { if (!off) setShots(Array.isArray(r.data?.items) ? r.data.items : []); })
      .catch(() => { if (!off) setShots(null); });
    return () => { off = true; };
  }, [profileId, readOnly]);
  const onShotItems = (items) => { setShots(items); onChanged && onChanged(); };

  const live = Array.isArray(steps);
  const list = live
    ? steps.map((s) => ({ ...(stepDef(s.key) || {}), ...s, name: s.name || (stepDef(s.key) || {}).name, standard: s.standard || (stepDef(s.key) || {}).standard }))
    : SOP_STEPS.map((d) => ({ ...d, status: null }));
  const shownRound = viewRound || round;
  const isRoundStep = (s) => (s.round_step != null ? !!s.round_step : !!(stepDef(s.key) || {}).round);
  const startSteps = list.filter((s) => !isRoundStep(s));
  const roundSteps = list.filter(isRoundStep);
  const startDone = live && startSteps.length > 0 && startSteps.every((s) => s.status === 'done');
  const outcomeOf = (n) => { const r = asArray(rounds).find((x) => Number(x.round) === Number(n)); return r ? r.outcome : null; };
  const shownOutcome = outcomeOf(shownRound);
  const inExclusive = live && !serviceClosed && !!exclusiveSince && shownRound === round;
  const closeReady = inExclusive && closeDaysLeft === 0;
  const focusStart = open && startSteps.some((s) => s.key === open);

  const startTick = (s, mode = 'tick') => {
    setOpen(s.key);
    setForm({ key: s.key, mode, note: mode === 'note' ? (s.note || '') : '', images: [], verified_by: '', choice: mode === 'ov_done' ? (s.choice || '') : '' });
  };
  const fail = (err) => toast.error(err?.response?.data?.error || t('操作失败', 'Failed'));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !form) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files.slice(0, Math.max(0, 9 - form.images.length))) {
        const r = await coachDailyContactsAPI.uploadImage(f);
        const u = r.data?.url || r.data?.file_url;
        if (u) urls.push(u);
      }
      setForm((x) => (x ? { ...x, images: [...x.images, ...urls] } : x));
    } catch (err) {
      toast.error(t('图片上传失败', 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // 管理员: 标记完成 / 不算完成 (override) and 编辑备注.
  const submitAdmin = async (s) => {
    const def = stepDef(s.key) || {};
    const note = form.note.trim();
    const round = isRoundStep(s) ? shownRound : undefined;
    if (form.mode === 'ov_done' && def.choice && !form.choice) { toast.error(t('请选择：复盘 还是 已约第二次', 'Pick one')); return; }
    setBusy(true);
    try {
      if (form.mode === 'note') {
        await workspaceAPI.editStepNote(profileId, s.key, note, round);
        toast.success(t('备注已保存', 'Note saved'));
      } else {
        await workspaceAPI.overrideStep(profileId, s.key, {
          override: form.mode === 'ov_done' ? 'done' : 'undone', round,
          note: note || undefined,
          images: form.mode === 'ov_done' && form.images.length ? form.images : undefined,
          choice: form.mode === 'ov_done' && def.choice ? form.choice : undefined,
        });
        toast.success(form.mode === 'ov_done' ? t('已由管理员标记完成', 'Marked done by admin') : t('已标记为不算完成', 'Marked as not done'));
      }
      setForm(null);
      onChanged && onChanged();
    } catch (err) { fail(err); } finally { setBusy(false); }
  };

  const clearOverride = async (s) => {
    const msg = s.mode === 'manual'
      ? t(`撤销管理员对「${s.name}」的修改？这一步会回到未完成。`, `Remove the admin change on "${s.name}"?`)
      : t(`恢复「${s.name}」的系统自动判断？`, `Restore the automatic result for "${s.name}"?`);
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await workspaceAPI.overrideStep(profileId, s.key, { override: null, round: isRoundStep(s) ? shownRound : undefined });
      toast.success(s.mode === 'manual' ? t('已撤销管理员修改', 'Admin change removed') : t('已恢复自动判断', 'Automatic result restored'));
      onChanged && onChanged();
    } catch (err) { fail(err); } finally { setBusy(false); }
  };

  const submit = async (s) => {
    if (!form) return;
    if (form.mode && form.mode !== 'tick') { await submitAdmin(s); return; }
    const def = stepDef(s.key) || {};
    if (def.needsVerifier) {
      if (!form.verified_by) { toast.error(t('请选择核实老师（第二位老师）', 'Pick the second teacher')); return; }
      if (Number(form.verified_by) === Number(meId)) { toast.error(t('核实老师不能是你自己', 'The second teacher must be someone else')); return; }
    }
    if (def.choice && !form.choice) { toast.error(t('请选择：复盘 还是 已约第二次', 'Pick one')); return; }
    const note = form.note.trim();
    setBusy(true);
    try {
      await workspaceAPI.tickStep(profileId, s.key, {
        round: isRoundStep(s) ? shownRound : undefined,
        note: note || undefined,
        images: form.images.length ? form.images : undefined,
        verified_by: def.needsVerifier ? Number(form.verified_by) : undefined,
        choice: def.choice ? form.choice : undefined,
      });
      toast.success(s.key === 'close' ? t('已结单', 'Service closed') : t('已标记完成', 'Marked done'));
      setForm(null);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('操作失败', 'Failed'));
    } finally { setBusy(false); }
  };

  const untick = async (s) => {
    const msg = s.key === 'close' ? t('撤销结单？会员会回到「一对一 · 待结单」。', 'Undo the close?') : t(`撤销「${s.name}」的完成标记？`, `Undo "${s.name}"?`);
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await workspaceAPI.untickStep(profileId, s.key, isRoundStep(s) ? shownRound : undefined);
      toast.success(t('已撤销', 'Undone'));
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('操作失败', 'Failed'));
    } finally { setBusy(false); }
  };

  const saveBreakup = async () => {
    const note = String(breakup || '').trim();
    if (!note) { toast.error(t('请写明分手原因', 'Please give the reason')); return; }
    setBusy(true);
    try {
      await workspaceAPI.tickStep(profileId, 'breakup', { note });
      toast.success(t('已记录，开始新一轮推荐', 'Recorded — a new round starts'));
      setBreakup(null);
      onRound && onRound(round); // back to 'current round'
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('操作失败', 'Failed'));
    } finally { setBusy(false); }
  };

  const undoBreakup = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('撤销这条分手记录？会员会回到上一轮的一对一。', 'Undo this breakup record?'))) return;
    setBusy(true);
    try {
      await workspaceAPI.untickStep(profileId, 'breakup');
      toast.success(t('已撤销', 'Undone'));
      onRound && onRound(round); // back to 'current round'
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('操作失败', 'Failed'));
    } finally { setBusy(false); }
  };

  const verifiers = teachers.filter((x) => Number(x.id) !== Number(meId));

  const renderRow = (s, idx) => {
    const st = STATUS[s.status] || null;
    const Icon = st ? st.Icon : Circle;
    const color = st ? st.color : '#A39E95';
    const isOpen = !readOnly && open === s.key;
    const def = stepDef(s.key) || {};
    const auto = s.mode ? s.mode !== 'manual' : (s.auto != null ? !!s.auto : !!def.auto);
    // The API says whether this viewer may tick (auto-only steps 400).
    const canTick = live && s.status !== 'done' && (s.can_tick != null ? !!s.can_tick : (!auto || def.manualToo));
    const go = GO[s.key];
    const ev = s.evidence && typeof s.evidence === 'object' ? s.evidence : {};
    let due = s.status === 'done' || s.status === 'skipped' ? '' : dueText(s.due_at, s.status === 'overdue', isZh);
    if (s.key === 'close' && s.status === 'waiting' && ev.days_left != null) due = t(`还有 ${ev.days_left} 天`, `${ev.days_left} days left`);
    if (s.key === 'close' && s.status === 'current') due = t('已满 3 个月，可以结单', '3 months reached');
    if (s.key === 'close' && s.status === 'todo') due = t('进入一对一后开始计时', 'Starts after 一对一');
    const imgs = asArray(s.images);
    const hot = s.status === 'current' || s.status === 'overdue';
    // Admin-overridden rows are undone with 恢复自动判断 (admins only), not with 撤销完成.
    const canUndo = live && s.status === 'done' && !s.override && (s.done_source ? s.done_source === 'manual' : !auto)
      && (s.key === 'close' ? isSup && shownRound === round : (isSup || s.done_by == null || Number(s.done_by) === Number(meId)));
    const admin = live && !!s.can_override;
    const fm = form && form.key === s.key ? (form.mode || 'tick') : null;
    const ovTip = s.override ? [s.override_text, s.override_at ? fmtMDHM(s.override_at) : ''].filter(Boolean).join(' · ') : '';
    return (
      <div key={s.key} data-tour={`ws-step-${s.key}`} style={{ borderTop: idx ? `1px solid ${S.line}` : 'none' }}>
        <button type="button" onClick={() => { if (!readOnly) setOpen(isOpen ? null : s.key); }} title={readOnly ? (s.standard || def.standard || '') : undefined}
          style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '13px 16px', background: hot ? '#EFEAE2' : 'none', border: 'none', cursor: readOnly ? 'default' : 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
          <Icon size={20} color={color} strokeWidth={s.status === 'current' ? 2.6 : 2} style={{ flex: '0 0 auto' }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14.5, fontWeight: hot ? 500 : 400, color: s.status === 'done' || s.status === 'skipped' ? S.sub : S.ink }}>
              {s.name}
              {auto && <Zap size={12} color={S.mute} />}
              {s.override && (
                <span title={ovTip} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 8px', borderRadius: 2, fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
                  background: s.override === 'done' ? '#EFEAE2' : '#F3EADB', color: s.override === 'done' ? '#3A3835' : '#93591A' }}>
                  <ShieldCheck size={11} />{s.override === 'done' ? t('管理员已改 · 手动完成', 'Admin · marked done') : t('管理员已改 · 不算完成', 'Admin · not done')}
                </span>
              )}
            </span>
            <span style={{ display: 'block', fontSize: 12, color: S.mute, marginTop: 2 }}>
              {[s.owner || def.owner, s.status === 'done'
                ? t(`${fmtMDHM(s.done_at)} 完成${s.done_by_name ? ` · ${s.done_by_name}` : ''}`, `Done ${fmtMDHM(s.done_at)}`)
                : (due || ((s.deadline || def.deadline) ? t(`时限：${s.deadline || def.deadline}`, `Due: ${s.deadline || def.deadline}`) : ''))].filter(Boolean).join(' · ')}
            </span>
          </span>
          {st && s.status !== 'todo' && <span style={{ fontSize: 12, fontWeight: 500, color, flex: '0 0 auto' }}>{isZh ? st.zh : st.en}</span>}
          {!readOnly && <ChevronDown size={15} color={S.mute} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s', flex: '0 0 auto' }} />}
        </button>
        {isOpen && (
          <div style={{ padding: '0 16px 16px 48px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ background: S.soft, borderRadius: 2, padding: '10px 12px' }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: S.sub, marginBottom: 3 }}>{t('完成标准', 'Standard')}</div>
              <div style={{ fontSize: 13.5, color: S.ink, lineHeight: 1.7 }}>{s.standard}</div>
            </div>
            {s.status === 'skipped' && (
              <div style={{ fontSize: 12.5, color: S.sub }}>{t('这一轮没有走到这一步（已复盘 / 已进入下一步 / 已分手）。', 'Not reached in this round.')}</div>
            )}
            {auto && (
              <div style={{ fontSize: 12.5, color: S.sub, display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <Zap size={12} style={{ flex: '0 0 auto' }} />{t('系统自动判断：', 'Automatic: ')}{s.auto_text || def.autoRule || ''}
              </div>
            )}
            {s.key === 'close' && ev.close_due_at && s.status !== 'done' && (
              <div style={{ fontSize: 12.5, color: S.sub }}>
                {t(`一对一开始：${fmtYMD(ev.exclusive_since)} · 满 3 个月：${fmtYMD(ev.close_due_at)}`, `一对一 since ${fmtYMD(ev.exclusive_since)} · 3 months on ${fmtYMD(ev.close_due_at)}`)}
              </div>
            )}
            {s.status === 'done' && (s.note || s.verified_by_name || s.choice || imgs.length > 0) && (
              <div style={{ fontSize: 13, color: S.ink, lineHeight: 1.6 }}>
                {s.choice && <div>{s.choice === 'second' ? t('有好感，已约第二次', 'Second date booked') : t('不合适，已复盘', 'Reviewed')}</div>}
                {s.verified_by_name && <div>{t(`核实老师：${s.verified_by_name}`, `Verified by ${s.verified_by_name}`)}</div>}
                {s.note && <div style={{ overflowWrap: 'anywhere' }}>{s.note}</div>}
                {s.note_updated_at && (
                  <div style={{ fontSize: 12, color: S.mute }}>{t(`备注 ${fmtMDHM(s.note_updated_at)} 由 ${s.note_updated_by_name || '—'} 修改`, `Note edited ${fmtMDHM(s.note_updated_at)}`)}</div>
                )}
                {imgs.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    {imgs.map((u, i) => (
                      <a key={i} href={resolvePhotoUrl(u) || u} target="_blank" rel="noreferrer">
                        <img src={resolvePhotoUrl(u) || u} alt="" style={{ width: 56, height: 72, objectFit: 'cover', borderRadius: 2, border: `1px solid ${S.line}` }} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {s.override && (
              <div style={{ fontSize: 12.5, color: S.sub, display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <ShieldCheck size={12} style={{ flex: '0 0 auto' }} />
                <span style={{ overflowWrap: 'anywhere' }}>
                  {ovTip || t('管理员已修改', 'Changed by an admin')}
                  {s.override === 'undone' && s.note ? t(`：${s.note}`, `: ${s.note}`) : ''}
                </span>
              </div>
            )}

            {fm ? (
              <div style={{ border: `1px solid ${S.line}`, borderRadius: 2, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, background: '#FFFDF9' }}>
                {fm !== 'tick' && (
                  <div style={{ fontSize: 12.5, fontWeight: 500, color: S.sub, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <ShieldCheck size={13} />
                    {fm === 'ov_done' ? t('管理员标记完成（不需要系统条件）', 'Admin: mark done')
                      : fm === 'ov_undone' ? t('管理员：这一步不算完成（即使系统判断已完成）', 'Admin: count as not done') : t('编辑备注', 'Edit note')}
                  </div>
                )}
                {def.choice && (fm === 'tick' || fm === 'ov_done') && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {[['review', t('不合适，已复盘', 'Reviewed (not a fit)')], ['second', t('有好感，已约第二次', 'Second date booked')]].map(([c, label]) => (
                        <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${form.choice === c ? '#111111' : S.line}`, borderRadius: 2, padding: '7px 10px', fontSize: 13, cursor: 'pointer', background: form.choice === c ? '#EFEAE2' : '#FFFDF9' }}>
                          <input type="radio" checked={form.choice === c} onChange={() => setForm((x) => ({ ...x, choice: c }))} style={{ accentColor: '#111111' }} />{label}
                        </label>
                      ))}
                    </div>
                    {form.choice === 'review' && <div style={{ fontSize: 12, color: S.sub }}>{t('这一轮结束，下一轮从「两位老师核实推荐」重新开始。', 'This round ends; the next round starts again at the verified picks.')}</div>}
                    {form.choice === 'second' && <div style={{ fontSize: 12, color: S.sub }}>{t('这一轮继续，下一步：进入一对一。', 'The round goes on — next: 一对一.')}</div>}
                  </div>
                )}
                {def.needsVerifier && fm === 'tick' && (
                  <label style={{ fontSize: 12.5, color: S.sub, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {t('核实老师（第二位老师，确认 3 位人选都有推荐理由）', 'Second teacher who checked the 3 picks')}
                    <select value={form.verified_by} onChange={(e) => setForm((x) => ({ ...x, verified_by: e.target.value }))} style={field}>
                      <option value="">{t('请选择', 'Choose…')}</option>
                      {verifiers.map((v) => <option key={v.id} value={String(v.id)}>{v.name}</option>)}
                    </select>
                  </label>
                )}
                {s.key === 'close' && (fm === 'tick' || fm === 'ov_done') && <div style={{ fontSize: 12.5, color: S.sub }}>{t('确认后服务完成（已结单），不再出现在今日跟进，也不再生成月度报告。', 'After this the service is complete.')}</div>}
                {s.key === 'close' && fm === 'ov_undone' && <div style={{ fontSize: 12.5, color: S.sub }}>{t('结单会被取消，服务重新进行中。', 'The service is re-opened.')}</div>}
                <textarea value={form.note} rows={3} maxLength={1000} onChange={(e) => setForm((x) => ({ ...x, note: e.target.value }))}
                  placeholder={fm === 'ov_undone' ? t('原因（选填）：为什么不算完成', 'Reason (optional)')
                    : fm === 'note' ? t('备注', 'Note')
                      : def.notePrompt ? t(`备注：${def.notePrompt}`, 'Note') : t('备注（选填）：做了什么、结果', 'Note (optional)')}
                  style={{ ...field, resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {(fm === 'tick' || fm === 'ov_done') && form.images.map((u, i) => (
                    <div key={u + i} style={{ position: 'relative', width: 48, height: 64, borderRadius: 2, overflow: 'hidden', border: `1px solid ${S.line}` }}>
                      <img src={resolvePhotoUrl(u) || u} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button type="button" aria-label={t('移除', 'Remove')} onClick={() => setForm((x) => ({ ...x, images: x.images.filter((_, j) => j !== i) }))}
                        style={{ position: 'absolute', top: 2, right: 2, width: 16, height: 16, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.6)', color: '#FFFDF9', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={10} /></button>
                    </div>
                  ))}
                  {(fm === 'tick' || fm === 'ov_done') && (
                    <>
                      <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} style={{ display: 'none' }} />
                      <button type="button" disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()} style={{ ...btnGhost, height: 30, fontSize: 12.5 }}>
                        <ImageIcon size={13} />{uploading ? t('上传中…', 'Uploading…') : t('加截图（选填）', 'Screenshots')}
                      </button>
                      {form.images.length < 9 && (
                        <QrUploadButton profileId={profileId} isZh={isZh} target={{ kind: 'step_note', step_key: s.key }}
                          where={`${memberName || '—'} · ${s.name || ''} · ${t('截图', 'screenshots')}`}
                          onFiles={(urls) => setForm((x) => (x ? { ...x, images: [...x.images, ...urls].slice(0, 9) } : x))} />
                      )}
                    </>
                  )}
                  <span style={{ flex: 1 }} />
                  <button type="button" onClick={() => setForm(null)} style={{ ...btnLink, color: S.sub }}>{t('取消', 'Cancel')}</button>
                  <button type="button" disabled={busy} onClick={() => submit(s)} style={btnPrimary(busy)}>
                    {fm === 'note' ? t('保存备注', 'Save note')
                      : fm === 'ov_undone' ? t('确认不算完成', 'Confirm')
                        : s.key === 'close' ? t('确认结单', 'Close service') : t('确认完成', 'Confirm')}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {canTick && (
                  <button type="button" onClick={() => startTick(s)} style={btnPrimary(false)}>
                    {s.key === 'close' ? <Flag size={14} /> : <CheckCircle2 size={14} />}{s.key === 'close' ? t('确认结单', 'Close service') : t('标记完成', 'Mark done')}
                  </button>
                )}
                {go && s.status !== 'done' && s.status !== 'skipped' && onGo && (
                  <button type="button" onClick={() => onGo(go[0])} style={btnGhost}>{isZh ? go[1] : go[2]}</button>
                )}
                {canUndo && (
                  <button type="button" disabled={busy} onClick={() => untick(s)} style={{ ...btnLink, color: S.sub }}>{s.key === 'close' ? t('撤销结单', 'Undo close') : t('撤销完成', 'Undo')}</button>
                )}
                {!admin && s.can_edit_note && s.status === 'done' && (
                  <button type="button" onClick={() => startTick(s, 'note')} style={{ ...btnLink, color: S.sub, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Pencil size={12} />{t('编辑备注', 'Edit note')}
                  </button>
                )}
              </div>
            )}

            {/* 电话已完成但还缺双方截图 → the step only counts once both screenshots are in. */}
            {s.status !== 'done' && s.evidence && s.evidence.waiting_shots && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#9E2B25' }}>
                {t('电话已记录，上传双方截图后才算完成', 'Call logged — upload screenshots from both sides to complete')}
              </div>
            )}

            {/* 双方截图: 本会员 / 对方 per recommended pair; both needed before 标记完成 (hand tick and auto rule). */}
            {live && shots && SHOT_STEP_KEYS.includes(s.key) && !fm && (
              <StepShots profileId={profileId} stepKey={s.key} pairs={asArray(roundMatches)} items={shots} memberName={memberName}
                meId={meId} isSup={isSup} isZh={isZh} disabled={serviceClosed || s.status === 'skipped'} onItems={onShotItems} />
            )}

            {/* 复盘 / 约第二次: write the 3-day review, or update this round's
                recommendation statuses (all 已拒绝 → straight into the next round). */}
            {live && s.key === 'review_or_second' && s.status !== 'done' && !fm && !serviceClosed && shownRound === round && (
              <ReviewRoundTools profileId={profileId} round={round} roundMatches={roundMatches} onChanged={onChanged} />
            )}
            {/* 已约会过的推荐对象 → 安排第 N 次约会 (also after 有好感 · 已约第二次). */}
            {live && s.key === 'review_or_second' && !fm && !serviceClosed && shownRound === round && (
              <NextDatePanel profileId={profileId} memberName={memberName} isZh={isZh} TimeInput={TimeInput} onChanged={onChanged} />
            )}

            {admin && !fm && (
              <div style={{ border: `1px dashed ${S.line}`, borderRadius: 2, padding: '8px 12px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', background: '#EFEAE2' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 500, color: S.sub, marginRight: 4 }}>
                  <ShieldCheck size={13} />{t('管理员操作', 'Admin')}
                </span>
                {s.status !== 'done' && !canTick && (
                  <button type="button" disabled={busy} onClick={() => startTick(s, 'ov_done')} style={{ ...btnGhost, height: 30, fontSize: 12.5 }}>
                    <CheckCircle2 size={13} />{t('标记完成', 'Mark done')}
                  </button>
                )}
                {s.status === 'done' && (
                  <button type="button" disabled={busy} onClick={() => startTick(s, 'ov_undone')} style={{ ...btnGhost, height: 30, fontSize: 12.5 }}>
                    <Ban size={13} />{t('不算完成', 'Not done')}
                  </button>
                )}
                {s.override && (
                  <button type="button" disabled={busy} onClick={() => clearOverride(s)} style={{ ...btnGhost, height: 30, fontSize: 12.5 }}>
                    <RotateCcw size={13} />{s.mode === 'manual' ? t('撤销管理员修改', 'Remove admin change') : t('恢复自动判断', 'Restore automatic')}
                  </button>
                )}
                {s.can_edit_note && s.status === 'done' && (
                  <button type="button" disabled={busy} onClick={() => startTick(s, 'note')} style={{ ...btnLink, color: S.sub, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Pencil size={12} />{t('编辑备注', 'Edit note')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const pill = (text, style) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 2, fontSize: 12, fontWeight: 500, background: style.bg, color: style.fg, whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
  );

  return (
    <div>
      {readOnly && (
        <div style={{ ...card, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: S.sub, background: S.soft }}>
          {t('这位会员不是你负责的客户，这里只显示服务进度（完成到哪一步），不能标记或查看备注。', 'Not your member — progress only, read-only.')}
        </div>
      )}
      {!readOnly && <TabIntro isZh={isZh} explain={isZh ? TAB_EXPLAIN.steps : ''}
        text={t('开始阶段做一次；推荐从「两位老师核实」到「进入一对一」，不合适或分手就重新来一轮。', 'Start once; each round runs from verified picks to 一对一, and repeats if it does not work out.')}
        steps={[
          t('红色 = 已逾期，黑色圆圈 = 现在该做的一步；点开看「完成标准」，做到了再标记完成。', 'Red = overdue, black ring = now. Open a step, meet the standard, mark it done.'),
          t('见面后选「不合适 · 已复盘」→ 开始第 2 轮推荐；选「有好感 · 已约第二次」→ 继续走到进入一对一。', '“Reviewed” starts the next round; “second date” keeps going to 一对一.'),
          t('进入一对一后有 3 个月关系稳定期：满 3 个月确认结单；中途分手写明原因，重新推荐。', '一对一 → 3 months → close. A breakup (with reason) starts a new round.'),
        ]} />}

      {serviceClosed && (
        <div style={{ ...card, padding: '14px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, background: '#E6EEE8', borderColor: 'rgba(63,125,90,.3)' }}>
          <CheckCircle2 size={22} color="#3F7D5A" style={{ flex: '0 0 auto' }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: S.serif, fontSize: 22, fontWeight: 500, color: '#3F7D5A' }}>{t(`已结单 · ${fmtYMD(closedAt)}`, `Closed · ${fmtYMD(closedAt)}`)}</div>
            <div style={{ fontSize: 12.5, color: '#3F7D5A', marginTop: 2 }}>{t('一对一满 3 个月，服务已完成。不再出现在今日跟进，也不再生成月度报告。', 'Service complete.')}</div>
          </div>
        </div>
      )}

      {!live && <div style={{ marginBottom: 12 }}><NotDeployed isZh={isZh} what={t('每一步的完成情况', 'Step progress')} /></div>}

      {/* 开始阶段 — once */}
      {startDone && !startOpen && !focusStart ? (
        <button type="button" onClick={() => setStartOpen(true)}
          style={{ ...card, width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', marginBottom: 16, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
          <CheckCircle2 size={18} color="#3F7D5A" />
          <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: S.sub }}>{t('开始阶段 ✓', 'Getting started ✓')}</span>
          <span style={{ fontSize: 12, color: S.mute }}>{t(`${startSteps.length} 步已完成 · 展开`, 'Show')}</span>
          <ChevronDown size={15} color={S.mute} />
        </button>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px' }}>
            <span style={{ fontFamily: S.serif, fontSize: 24, fontWeight: 500, lineHeight: 1.2, color: S.ink }}>{t('开始阶段', 'Getting started')}</span>
            <span style={{ fontSize: 12, color: S.mute }}>{t('只做一次', 'once')}</span>
            {startDone && <button type="button" onClick={() => setStartOpen(false)} style={{ ...btnLink, color: S.sub, marginLeft: 'auto' }}>{t('收起', 'Collapse')}</button>}
          </div>
          <div style={{ ...card, padding: '4px 0' }}>{startSteps.map(renderRow)}</div>
        </div>
      )}

      {/* 第 N 轮推荐 — repeats */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 8px' }}>
        <span style={{ fontFamily: S.serif, fontSize: 24, fontWeight: 500, lineHeight: 1.2, color: S.ink }}>{t(`第 ${shownRound} 轮推荐`, `Round ${shownRound}`)}</span>
        {shownOutcome ? pill(shownOutcome.kind === 'review' ? t('不合适 · 已复盘', 'Not a fit') : shownOutcome.text, OUTCOME_STYLE[shownOutcome.kind] || OUTCOME_STYLE.review)
          : <span style={{ fontSize: 12, color: S.mute }}>{t('两位老师核实 → 一对一', 'verified picks → 一对一')}</span>}
      </div>
      {round > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
          {Array.from({ length: round }, (_, i) => i + 1).map((n) => {
            const on = n === shownRound;
            const oc = outcomeOf(n);
            const tag = oc ? (oc.kind === 'review' ? t('复盘', 'review') : oc.kind === 'breakup' ? t('分手', 'breakup') : t('结单', 'closed')) : (n === round ? t('当前', 'now') : '');
            return (
              <button key={n} type="button" onClick={() => onRound && onRound(n)}
                style={{ height: 28, padding: '0 12px', borderRadius: 2, fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                  border: `1px solid ${on ? '#111111' : S.line}`, background: on ? '#EFEAE2' : '#FFFDF9', color: on ? '#111111' : S.sub }}>
                {t(`第 ${n} 轮`, `Round ${n}`)}{tag ? t(`（${tag}）`, ` (${tag})`) : ''}
              </button>
            );
          })}
        </div>
      )}
      {shownOutcome && shownOutcome.kind === 'breakup' && (
        <div style={{ fontSize: 12.5, color: S.sub, margin: '0 0 10px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{t(`${fmtYMD(shownOutcome.at)} 一对一中途结束，下一轮重新推荐。`, `Ended ${fmtYMD(shownOutcome.at)}.`)}</span>
          {!readOnly && isSup && shownRound === round - 1 && !serviceClosed && (
            <button type="button" disabled={busy} onClick={undoBreakup} style={{ ...btnLink, color: S.sub }}>{t('撤销分手记录', 'Undo breakup')}</button>
          )}
        </div>
      )}

      {/* 关系稳定期 countdown + 分手 */}
      {inExclusive && (
        <div style={{ ...card, padding: '14px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: closeReady ? '#F3EADB' : '#EFEAE2' }}>
          <Clock size={20} color="#93591A" style={{ flex: '0 0 auto' }} />
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 500, color: S.ink }}>
              {closeReady ? t('一对一已满 3 个月，可以确认结单', '3 months reached — ready to close') : t(`关系稳定期 · 还有 ${closeDaysLeft} 天`, `Settling in · ${closeDaysLeft} days left`)}
            </div>
            <div style={{ fontSize: 12.5, color: S.sub, marginTop: 2 }}>
              {t(`一对一开始 ${fmtYMD(exclusiveSince)} · ${fmtYMD(closeDueAt)} 满 3 个月`, `Since ${fmtYMD(exclusiveSince)} · 3 months on ${fmtYMD(closeDueAt)}`)}
            </div>
          </div>
          {!readOnly && closeReady && (
            <button type="button" onClick={() => { const c = roundSteps.find((x) => x.key === 'close'); if (c) startTick(c); }} style={btnPrimary(false)}>
              <Flag size={14} />{t('确认结单', 'Close service')}
            </button>
          )}
          {!readOnly && (
          <button type="button" onClick={() => setBreakup('')} style={{ ...btnGhost, color: '#9E2B25' }}>
            <HeartCrack size={14} />{t('一对一中途结束（分手）· 重新推荐', 'Ended early — recommend again')}
          </button>
          )}
        </div>
      )}

      <div style={{ ...card, padding: '4px 0' }}>{roundSteps.map(renderRow)}</div>

      {breakup !== null && (
        <div role="dialog" aria-modal="true" onClick={() => !busy && setBreakup(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(30,20,20,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 440, padding: 18, display: 'flex', flexDirection: 'column', gap: 12, background: '#FFFDF9' }}>
            <div style={{ fontFamily: S.serif, fontSize: 22, fontWeight: 500, color: S.ink }}>{t('一对一中途结束（分手）', 'One-to-one ended early')}</div>
            <div style={{ fontSize: 13, color: S.sub, lineHeight: 1.6 }}>
              {t(`记录后：第 ${round} 轮结束，3 个月倒计时取消，第 ${round + 1} 轮从「两位老师核实推荐」重新开始。`, `Round ${round} ends, the countdown stops and round ${round + 1} starts.`)}
            </div>
            <textarea autoFocus value={breakup} rows={4} maxLength={1000} onChange={(e) => setBreakup(e.target.value)}
              placeholder={t('分手原因（必填）：谁提出的、为什么、会员现在的想法', 'Reason (required)')} style={{ ...field, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              <button type="button" disabled={busy} onClick={() => setBreakup(null)} style={{ ...btnLink, color: S.sub }}>{t('取消', 'Cancel')}</button>
              <button type="button" disabled={busy || !String(breakup).trim()} onClick={saveBreakup} style={{ ...btnPrimary(busy), opacity: busy || !String(breakup).trim() ? 0.6 : 1 }}>
                {t('确认，重新推荐', 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
